export interface RareEarthElement {
  id: string
  name: string
  symbol: string
  atomicNumber: number
  category: 'light' | 'heavy'
  description: string
  applications: string[]
  atomicMass: string
  density: string
  meltingPoint: string
  boilingPoint: string
  electronConfig: string
  phase: string
}

export const RARE_EARTH_ELEMENTS: RareEarthElement[] = [
  {
    id: 'lanthanum',
    name: 'Lanthanum',
    symbol: 'La',
    atomicNumber: 57,
    category: 'light',
    atomicMass: '138.91',
    density: '6.15 g/cm³',
    meltingPoint: '919 °C',
    boilingPoint: '3464 °C',
    electronConfig: '[Xe] 5d¹ 6s²',
    phase: 'Solid',
    description: 'Lanthanum is a soft, ductile, silvery-white metal that tarnishes rapidly in air. It is the first element in the lanthanide series and one of the most abundant rare earth elements.',
    applications: [
      'Petroleum refining catalysts',
      'Nickel-metal hydride (NiMH) batteries',
      'Camera and telescope lenses',
      'Hydrogen storage materials',
      'Carbon arc lamps',
    ],
  },
  {
    id: 'cerium',
    name: 'Cerium',
    symbol: 'Ce',
    atomicNumber: 58,
    category: 'light',
    atomicMass: '140.12',
    density: '6.77 g/cm³',
    meltingPoint: '798 °C',
    boilingPoint: '3443 °C',
    electronConfig: '[Xe] 4f¹ 5d¹ 6s²',
    phase: 'Solid',
    description: 'Cerium is the most abundant of the rare earth elements. It is a soft, silvery, ductile metal that oxidizes in air and is used in numerous industrial applications.',
    applications: [
      'Catalytic converters for automotive emissions',
      'Glass polishing compounds',
      'White LED phosphors',
      'Ferrocerium (lighter flints)',
      'Self-cleaning ovens',
    ],
  },
  {
    id: 'praseodymium',
    name: 'Praseodymium',
    symbol: 'Pr',
    atomicNumber: 59,
    category: 'light',
    atomicMass: '140.91',
    density: '6.77 g/cm³',
    meltingPoint: '931 °C',
    boilingPoint: '3520 °C',
    electronConfig: '[Xe] 4f³ 6s²',
    phase: 'Solid',
    description: 'Praseodymium is a soft, silvery, malleable metal. It is used in combination with neodymium to create powerful permanent magnets and in various optical applications.',
    applications: [
      'Neodymium-iron-boron (NdFeB) magnets',
      'High-strength alloys for aircraft engines',
      'Yellow-green glass and ceramics',
      'Fiber optic amplifiers',
      'Welding goggles',
    ],
  },
  {
    id: 'neodymium',
    name: 'Neodymium',
    symbol: 'Nd',
    atomicNumber: 60,
    category: 'light',
    atomicMass: '144.24',
    density: '7.01 g/cm³',
    meltingPoint: '1021 °C',
    boilingPoint: '3074 °C',
    electronConfig: '[Xe] 4f⁴ 6s²',
    phase: 'Solid',
    description: 'Neodymium is essential for the strongest permanent magnets known. NdFeB magnets are critical for electric vehicles, wind turbines, and many consumer electronics.',
    applications: [
      'Neodymium-iron-boron (NdFeB) magnets',
      'Electric vehicle motors',
      'Wind turbine generators',
      'Headphones and speakers',
      'Hard disk drives',
    ],
  },
  {
    id: 'promethium',
    name: 'Promethium',
    symbol: 'Pm',
    atomicNumber: 61,
    category: 'light',
    atomicMass: '145',
    density: '7.26 g/cm³',
    meltingPoint: '1042 °C',
    boilingPoint: '3000 °C',
    electronConfig: '[Xe] 4f⁵ 6s²',
    phase: 'Solid',
    description: 'Promethium is a radioactive rare earth element that does not occur naturally in significant quantities. It is produced in nuclear reactors and used in nuclear batteries and luminous paint.',
    applications: [
      'Nuclear batteries (pacemakers, spacecraft)',
      'Luminous paint for dials and signs',
      'Portable X-ray sources',
      'Thickness gauges',
      'Laser research',
    ],
  },
  {
    id: 'samarium',
    name: 'Samarium',
    symbol: 'Sm',
    atomicNumber: 62,
    category: 'light',
    atomicMass: '150.36',
    density: '7.52 g/cm³',
    meltingPoint: '1074 °C',
    boilingPoint: '1794 °C',
    electronConfig: '[Xe] 4f⁶ 6s²',
    phase: 'Solid',
    description: 'Samarium is used in samarium-cobalt magnets that maintain magnetization at high temperatures. It also has important medical and nuclear applications.',
    applications: [
      'Samarium-cobalt magnets (high-temperature)',
      'Cancer treatment (Samarium-153)',
      'Nuclear reactor control rods',
      'Chemical reducing agents',
      'X-ray lasers',
    ],
  },
  {
    id: 'europium',
    name: 'Europium',
    symbol: 'Eu',
    atomicNumber: 63,
    category: 'light',
    atomicMass: '151.96',
    density: '5.24 g/cm³',
    meltingPoint: '822 °C',
    boilingPoint: '1527 °C',
    electronConfig: '[Xe] 4f⁷ 6s²',
    phase: 'Solid',
    description: 'Europium is valued for its red and blue phosphors, making it essential for display technologies including LEDs, fluorescent lamps, and television screens.',
    applications: [
      'Red and blue phosphors in displays',
      'Euro banknote anti-counterfeiting',
      'Fluorescent lamps',
      'LED lighting',
      'MRI contrast agents',
    ],
  },
  {
    id: 'gadolinium',
    name: 'Gadolinium',
    symbol: 'Gd',
    atomicNumber: 64,
    category: 'light',
    atomicMass: '157.25',
    density: '7.90 g/cm³',
    meltingPoint: '1313 °C',
    boilingPoint: '3273 °C',
    electronConfig: '[Xe] 4f⁷ 5d¹ 6s²',
    phase: 'Solid',
    description: 'Gadolinium has the highest neutron capture cross-section of any element. It is widely used in MRI contrast agents and in various electronic applications.',
    applications: [
      'MRI contrast agents',
      'Microwave applications',
      'Neutron radiography',
      'Compact discs and computer memory',
      'X-ray and neutron shielding',
    ],
  },
  {
    id: 'terbium',
    name: 'Terbium',
    symbol: 'Tb',
    atomicNumber: 65,
    category: 'heavy',
    atomicMass: '158.93',
    density: '8.23 g/cm³',
    meltingPoint: '1356 °C',
    boilingPoint: '3230 °C',
    electronConfig: '[Xe] 4f⁹ 6s²',
    phase: 'Solid',
    description: 'Terbium is used in solid-state devices and as a dopant in materials that change shape in magnetic fields. It produces green phosphors for displays.',
    applications: [
      'Green phosphors in displays',
      'Terfenol-D (magnetostrictive alloy)',
      'Fluorescent lamps',
      'Solid-state devices',
      'Fuel cells',
    ],
  },
  {
    id: 'dysprosium',
    name: 'Dysprosium',
    symbol: 'Dy',
    atomicNumber: 66,
    category: 'heavy',
    atomicMass: '162.50',
    density: '8.54 g/cm³',
    meltingPoint: '1412 °C',
    boilingPoint: '2567 °C',
    electronConfig: '[Xe] 4f¹⁰ 6s²',
    phase: 'Solid',
    description: 'Dysprosium is added to NdFeB magnets to improve their performance at high temperatures, making it critical for electric vehicles and wind turbines.',
    applications: [
      'High-temperature NdFeB magnets',
      'Electric vehicle motors',
      'Wind turbine generators',
      'Nuclear control rods',
      'Data storage devices',
    ],
  },
  {
    id: 'holmium',
    name: 'Holmium',
    symbol: 'Ho',
    atomicNumber: 67,
    category: 'heavy',
    atomicMass: '164.93',
    density: '8.79 g/cm³',
    meltingPoint: '1474 °C',
    boilingPoint: '2700 °C',
    electronConfig: '[Xe] 4f¹¹ 6s²',
    phase: 'Solid',
    description: 'Holmium has the strongest magnetic moment of any element. It is used in magnets, lasers, and as a colorant for cubic zirconia and glass.',
    applications: [
      'High-strength magnets',
      'Medical and dental lasers',
      'Nuclear control rods',
      'Calibration of spectrophotometers',
      'Coloring glass and cubic zirconia',
    ],
  },
  {
    id: 'erbium',
    name: 'Erbium',
    symbol: 'Er',
    atomicNumber: 68,
    category: 'heavy',
    atomicMass: '167.26',
    density: '9.07 g/cm³',
    meltingPoint: '1529 °C',
    boilingPoint: '2868 °C',
    electronConfig: '[Xe] 4f¹² 6s²',
    phase: 'Solid',
    description: 'Erbium is essential for fiber optic amplifiers that enable long-distance telecommunications. It also produces a distinctive pink color in glass.',
    applications: [
      'Fiber optic amplifiers (internet backbone)',
      'Laser technology (medical, dental)',
      'Pink glass and ceramic colorant',
      'Nuclear technology',
      'Metallurgical alloys',
    ],
  },
  {
    id: 'thulium',
    name: 'Thulium',
    symbol: 'Tm',
    atomicNumber: 69,
    category: 'heavy',
    atomicMass: '168.93',
    density: '9.32 g/cm³',
    meltingPoint: '1545 °C',
    boilingPoint: '1950 °C',
    electronConfig: '[Xe] 4f¹³ 6s²',
    phase: 'Solid',
    description: 'Thulium is one of the least abundant rare earth elements. It is used in portable X-ray devices and as a dopant in solid-state lasers.',
    applications: [
      'Portable X-ray sources',
      'Solid-state lasers',
      'High-efficiency lighting',
      'Superconducting materials',
      'Medical imaging',
    ],
  },
  {
    id: 'ytterbium',
    name: 'Ytterbium',
    symbol: 'Yb',
    atomicNumber: 70,
    category: 'heavy',
    atomicMass: '173.05',
    density: '6.90 g/cm³',
    meltingPoint: '819 °C',
    boilingPoint: '1196 °C',
    electronConfig: '[Xe] 4f¹⁴ 6s²',
    phase: 'Solid',
    description: 'Ytterbium is used in atomic clocks, fiber optic amplifiers, and as a doping agent in stainless steel. It has one of the most stable isotopes.',
    applications: [
      'Atomic clocks (most precise timekeeping)',
      'Fiber optic amplifiers',
      'Stainless steel doping',
      'Solar cell efficiency',
      'Chemical catalysts',
    ],
  },
  {
    id: 'lutetium',
    name: 'Lutetium',
    symbol: 'Lu',
    atomicNumber: 71,
    category: 'heavy',
    atomicMass: '174.97',
    density: '9.84 g/cm³',
    meltingPoint: '1663 °C',
    boilingPoint: '3402 °C',
    electronConfig: '[Xe] 4f¹⁴ 5d¹ 6s²',
    phase: 'Solid',
    description: 'Lutetium is the last and heaviest of the lanthanides. It is used in PET scanners for cancer detection and as a catalyst in petroleum refining.',
    applications: [
      'PET scanner crystals (cancer detection)',
      'Petroleum refining catalysts',
      'LED phosphors',
      'High-refractive-index glass',
      'Single crystal scintillators',
    ],
  },
]
